import React, { useEffect, useState } from 'react';
import { subject } from '@casl/ability';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, AtsSkeleton, Typography, TYPOGRAPHY_WEIGHT } from '_atoms';
import {
  Approvers,
  PopupSubmitDecision,
  PopupSubmitOfferDecision,
  PopupWithdraw,
  PopupAssignToStep,
  PopupEditApprovers,
  JobCandidateInfo,
} from '_molecules';
import { Interview, InterviewFeedback, Resolution, Offer, Timeline } from '_organisms/index';
import { ACTIONS, UI, APPROVERS_ROLES, useAbility } from 'permission';
import { STEPS_LABELS, stepsStatuses } from 'constants/constants';
import { canShowAddInterview, getFeedbackHistorySteps, getInterviewKind, INTERVIEW_KINDS } from 'constants/interview';
import { setApprover } from 'actions';
import type { SaveInterviewPayload } from 'actions/jobActions';
import img from 'dist/emptyStates/stepsNoCandidates.svg';
import type {
  JobWorkflowStep,
  Opening as OpeningEntity,
  JobApprover,
  OfferingCandidate,
} from 'store/reducers/jobReducer';
import type { Uuid } from 'store/reducers/types';
import './styles.scss';

interface StepsRightPanelProps {
  openingCandidate?: OpeningEntity | null;
  activeStep: number;
  createInterview: (data: SaveInterviewPayload) => void;
  confirmInterview: (uuid: Uuid | undefined, cb: (value: boolean) => void) => void;
  submitDecisionWithOppening: (data: unknown) => void;
  openingsList: OpeningEntity[];
  step?: JobWorkflowStep | null;
  submitOffer: (data: Record<string, unknown>) => void;
  assignToStep: (stepId: Uuid | undefined) => void;
  steps: JobWorkflowStep[];
  empty?: boolean;
  author?: { uuid?: Uuid } | null;
  userData: Record<string, unknown>;
  approversCandidate: Array<JobApprover & { optional?: boolean }>;
  jobData: { status?: string; name?: string; service?: { uuid?: Uuid }; company?: { uuid?: Uuid } };
  submitDecision: (isFinalDecision: boolean, data: unknown) => void;
  actionWithdraw: (comment: string) => void;
  cancelInterview: (uuid: Uuid | undefined, data: unknown) => void;
  loading?: boolean;
  getCandidateStepAssignment: (
    jobId: string,
    candidateId: string,
    clear: boolean,
    steps: JobWorkflowStep[],
    activeStep: number
  ) => void;
  offeringCandidate: OfferingCandidate;
  entryDateLoading?: boolean;
  user: { profile?: { has_calendar_access?: boolean } };
  offeringCandidateLoading?: boolean;
  approversCandidateLoading?: boolean;
  updateCandidateResolution: (data: Record<string, unknown>) => void;
  getSteps: (jobId: string, clear: boolean) => void;
  getApprovers: (jobId: string, stepId: string, candidateId: string) => void;
  refreshCandidateStep: () => void;
  [key: string]: unknown;
}

const LoadingState: React.FC = () => (
  <Card className="right-panel">
    <div style={{ marginBottom: '12px' }}>
      <AtsSkeleton height={90} />
    </div>
    <div style={{ marginBottom: '12px' }}>
      <AtsSkeleton height={340} />
    </div>

    <div style={{ marginBottom: '12px' }}>
      <AtsSkeleton height={137} />
    </div>
  </Card>
);

const EmptyState: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Card className="right-panel right-panel__empty">
      <img src={img} alt="" />
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:steps_right_panel.title_no_candidate_selected')}
      </Typography.Title3>
      <Typography.Text>{t('translation:steps_right_panel.text_select_candidate')}</Typography.Text>
    </Card>
  );
};

export const StepsRightPanel: React.FC<StepsRightPanelProps> = props => {
  const {
    openingCandidate,
    activeStep,
    createInterview,
    confirmInterview,
    submitDecisionWithOppening,
    openingsList,
    step,
    submitOffer,
    assignToStep,
    steps,
    empty,
    author,
    userData,
    approversCandidate,
    jobData,
    submitDecision,
    actionWithdraw,
    cancelInterview,
    loading,
  } = props;

  const {
    id: jobId,
    step: stepId,
    candidate: candidateId,
  } = useParams<{
    id: string;
    step: string;
    candidate: string;
  }>();

  const [showPopupDecision, setShowPopupDecision] = useState(false);
  const [showPopupWithdraw, setShowPopupWithdraw] = useState(false);
  const [showPopupApprovers, setShowPopupApprovers] = useState(false);
  const [showPopupFinalDecision, setShowPopupFinalDecision] = useState(false);
  const [showPopupAssignToStep, setShowPopupAssignToStep] = useState(false);
  const [submitDecisionOffer, setSubmitDecisionOffer] = useState(false);
  const [summaryEditing, setSummaryEditing] = useState(false);
  const ability = useAbility();
  const rejected = userData.step_status === 'Rejected';
  const interviewKind = getInterviewKind(step?.name);
  const isHrInterviewStep = interviewKind === INTERVIEW_KINDS.HR;
  const canCreateInterview = !isHrInterviewStep || userData.step_status === stepsStatuses.waitForResolution;
  const canAddAdditional = canShowAddInterview(interviewKind, rejected, userData.step_status as string | undefined);
  const showHrInterviewDecisionWarning =
    showPopupDecision && isHrInterviewStep && userData.step_status === stepsStatuses.provideFeedback;
  const feedbackHistorySteps = getFeedbackHistorySteps(steps, step);

  const onCLoseWithdrawPopup = () => setShowPopupWithdraw(false);
  const onWithdraw = (comment: string) => {
    setShowPopupWithdraw(false);
    actionWithdraw(comment);
  };

  const onCloseSubmitOfferPopup = () => setSubmitDecisionOffer(false);
  const onSubmitOfferPopup = (data: unknown) => {
    setSubmitDecisionOffer(false);
    submitDecisionWithOppening(data);
  };

  useEffect(() => {
    setSummaryEditing(false);
    if (candidateId && !empty) {
      props.getCandidateStepAssignment(jobId, candidateId, true, steps, activeStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, empty, stepId]);

  const closePopups = () => {
    setShowPopupDecision(false);
    setShowPopupFinalDecision(false);
  };

  let offer;
  if (step) {
    if (step.name === STEPS_LABELS.OFFER) offer = !!Object.keys(props.offeringCandidate).length;
    if (step.name === STEPS_LABELS.ONBOARING) offer = true;
  }
  const newOpeningsList = [...openingsList];
  if (openingCandidate) newOpeningsList.push(openingCandidate);

  const defaultApprover = !!approversCandidate?.find(i => i.user.uuid === author?.uuid && i.is_default);

  const onSubmitDecision = (m: boolean, d: unknown) => {
    submitDecision(m, d);
    closePopups();
  };

  if (loading) {
    return <LoadingState />;
  }
  if (empty) {
    return <EmptyState />;
  }
  const editOffer = ability.can(ACTIONS.READ, subject(UI.SUBMIT_OFFER, { step: step?.name })) && !rejected;

  /* On the interview steps we drop the service scope so cross-service Team Leads become
     selectable as approvers - the backend then auto-assigns them the Temporary Interviewer role. */
  const isInterviewStep = interviewKind === INTERVIEW_KINDS.TECHNICAL || interviewKind === INTERVIEW_KINDS.MANAGEMENT;
  const approverRequestOptions = {
    role: APPROVERS_ROLES.join(','),
    ...(isInterviewStep ? {} : { service: jobData.service?.uuid }),
    company: jobData.company?.uuid,
    limit: 25,
  };

  return (
    <div className="right-panel-wrapper">
      <Card className={`right-panel${summaryEditing ? ' summary-editing' : ''}`}>
        <JobCandidateInfo
          steps={steps}
          userData={userData as never}
          author={author}
          approversCandidate={approversCandidate}
          setShowPopupDecision={setShowPopupDecision}
          setShowPopupWithdraw={setShowPopupWithdraw}
          openAddApprovers={() => setShowPopupApprovers(true)}
          setShowPopupFinalDecision={setShowPopupFinalDecision}
          setShowPopupAssignToStep={setShowPopupAssignToStep}
          setSubmitDecisionOffer={setSubmitDecisionOffer}
          summaryEditing={summaryEditing}
          onSummaryEditingChange={setSummaryEditing}
          offer={offer}
          step={step}
        />
        {!summaryEditing && (
          <>
            {activeStep >= 6 && !rejected && (
              <Resolution
                onSubmit={props.updateCandidateResolution}
                loading={props.entryDateLoading}
                openingCandidate={props.openingCandidate}
                userData={userData as never}
                jobName={jobData.name}
                jobStatus={jobData.status}
              />
            )}
            {activeStep >= 5 && (
              <Offer
                onSubmit={submitOffer}
                offer={props.offeringCandidate}
                editable={editOffer}
                defaultOpen={activeStep === 5}
                loading={props.offeringCandidateLoading}
              />
            )}
            {interviewKind && step && (
              <Interview
                accessToGoogleCalendar={props.user.profile?.has_calendar_access}
                cancelInterview={cancelInterview}
                canCreate={canCreateInterview}
                canAddAdditional={canAddAdditional}
                interviewKind={interviewKind}
                rejected={rejected}
                stepId={step.uuid}
                createInterview={createInterview}
                confirmInterview={confirmInterview}
                userData={userData as never}
                onFeedbackChanged={props.refreshCandidateStep}
                sourceStepName={step.name}
              />
            )}
            {feedbackHistorySteps.map(sourceStep => {
              const sourceInterviewKind = getInterviewKind(sourceStep.name);
              if (!sourceInterviewKind || !sourceStep.name || sourceStep.uuid === step?.uuid) return null;

              return (
                <InterviewFeedback
                  key={sourceStep.uuid}
                  defaultOpen={false}
                  interviewKind={sourceInterviewKind}
                  onFeedbackChanged={props.refreshCandidateStep}
                  sourceStepName={sourceStep.name}
                  stepId={sourceStep.uuid}
                />
              );
            })}
            <Approvers
              list={approversCandidate}
              loading={props.approversCandidateLoading}
              requestOptions={approverRequestOptions}
              defaultApprover={defaultApprover}
              responsibleRecruiter={userData.responsible_recruiter as never}
              rejected={rejected || (step?.ordering_number as number) >= 5}
            />
          </>
        )}
      </Card>
      {!summaryEditing && <Timeline candidateData={{}} isActivityFeed />}
      <PopupWithdraw
        open={showPopupWithdraw}
        userData={userData as never}
        onClose={onCLoseWithdrawPopup}
        onWithdraw={onWithdraw}
      />
      <PopupSubmitOfferDecision
        open={submitDecisionOffer}
        userData={userData as never}
        onClose={onCloseSubmitOfferPopup}
        onSubmit={onSubmitOfferPopup as never}
        isOnboarding={activeStep === 6}
      />
      <PopupSubmitDecision
        onSave={onSubmitDecision as never}
        open={showPopupDecision || showPopupFinalDecision}
        isFinalDecision={showPopupFinalDecision}
        userData={userData as never}
        onClose={closePopups}
        activeStep={activeStep}
        showHrInterviewWarning={showHrInterviewDecisionWarning}
      />
      <PopupAssignToStep
        save={s => {
          setShowPopupAssignToStep(false);
          assignToStep(s);
        }}
        stepsOptions={steps.filter(i => i.ordering_number !== 6)}
        open={showPopupAssignToStep}
        userData={userData as never}
        onClose={() => {
          setShowPopupAssignToStep(false);
        }}
      />
      <PopupEditApprovers
        open={showPopupApprovers}
        onClose={() => setShowPopupApprovers(false)}
        onSave={approversId => {
          setApprover(jobId, stepId, candidateId, approversId)
            .then(() => {
              props.getSteps(jobId, true);
              props.getApprovers(jobId, stepId, candidateId);
            })
            .finally(() => setShowPopupApprovers(false));
        }}
        approvers={approversCandidate}
        responsibleRecruiter={userData.responsible_recruiter as never}
        requestOptions={approverRequestOptions}
      />
    </div>
  );
};
